/*
 * Phonebook groups - the pure logic behind the phonebook store, list and dialogs
 *
 * - Customer groups (employee null) hold shared contacts, personal groups hold their employee's personal contacts
 * - Contacts reference groups by id only; names are always resolved through the group list, so a rename needs no
 *   change to the contacts
 */

import type { PbxPhonebookContact } from '@/TcxApi/PbxPhonebook'
import type { PbxPhonebookGroup } from '@/TcxApi/PbxPhonebookGroup'
import type { ErrorResponseDetail } from '@/TcxApi/ErrorResponse'

/** The phonebook list's type filter */
export type PhonebookListType = 'ALL' | 'SHARED' | 'PERSONAL'

/** Why a group request was refused - each has a phonebook.groups.error.<key> text */
export type PhonebookGroupErrorKey = 'nameTaken' | 'nameInvalid' | 'accessDenied' | 'notFound' | 'network' | 'unknown'

/** Danish collation, as the API sorts the groups */
const collator = new Intl.Collator('da', { sensitivity: 'base' })

/**
 * The ids of the groups a contact is in
 * @param contact The contact
 * @returns The ids - an older contact without the field is in none
 */
export function contactGroupIds(contact: Pick<PbxPhonebookContact, 'groups'>): string[] {
	return contact.groups ?? []
}

/**
 * Sort groups by name, as the API does
 * @param groups Groups to sort - the array is not changed
 * @returns A new, sorted array
 */
export function sortGroups(groups: PbxPhonebookGroup[]): PbxPhonebookGroup[] {
	return [...groups].sort((a, b) => collator.compare(a.name, b.name))
}

/**
 * The groups a contact of a kind can be in
 * @param groups All known groups
 * @param shared True for a shared contact, false for a personal one
 * @param employee The employee owning a personal contact (the user)
 * @returns The customer groups for a shared contact, the employee's own groups for a personal one
 */
export function groupsForKind(groups: PbxPhonebookGroup[], shared: boolean, employee: string | undefined): PbxPhonebookGroup[] {
	if (shared) { return groups.filter(group => !group.employee) }
	if (!employee) { return [] }
	return groups.filter(group => group.employee === employee)
}

/**
 * The groups the list's group filter offers, following its type filter
 * @param groups All known groups
 * @param type The type filter
 * @param employee The user's employee id
 * @returns Customer groups for SHARED, the user's groups for PERSONAL, both for ALL
 */
export function groupsForListType(groups: PbxPhonebookGroup[], type: PhonebookListType, employee: string | undefined): PbxPhonebookGroup[] {
	if (type === 'SHARED') { return groupsForKind(groups, true, employee) }
	if (type === 'PERSONAL') { return groupsForKind(groups, false, employee) }
	return groups.filter(group => !group.employee || group.employee === employee)
}

/**
 * The group filter to use
 * @param selected The chosen (or remembered) group, null/undefined/'' for all
 * @param available The groups the filter offers right now
 * @returns The group, or null (all) when it no longer exists or the type filter rules it out
 */
export function validGroupFilter(selected: string | null | undefined, available: PbxPhonebookGroup[]): string | null {
	if (!selected) { return null }
	return available.some(group => group._id === selected) ? selected : null
}

/**
 * The names of a contact's groups
 * @param ids The contact's group ids
 * @param byId The known groups by id
 * @returns The names, sorted - ids of unknown groups are left out
 */
export function groupNames(ids: string[] | null | undefined, byId: Map<string, PbxPhonebookGroup>): string[] {
	const names: string[] = []

	for (const id of ids ?? []) {
		const group = byId.get(id)

		if (group) { names.push(group.name) }
	}
	return names.sort(collator.compare)
}

/**
 * Take a deleted group out of the local contacts - the API sends no PBX_PHONEBOOK_UPDATED for them
 * @param contacts The contacts
 * @param id Id of the deleted group
 * @returns The contacts that were in the group
 */
export function stripGroup(contacts: PbxPhonebookContact[], id: string): PbxPhonebookContact[] {
	const changed: PbxPhonebookContact[] = []

	for (const contact of contacts) {
		if (contact.groups?.includes(id)) {
			contact.groups = contact.groups.filter(groupId => groupId !== id)
			changed.push(contact)
		}
	}
	return changed
}

/**
 * Count the contacts in each group
 * @param contacts The loaded contacts
 * @returns Number of contacts by group id - groups without contacts are missing
 */
export function groupCounts(contacts: PbxPhonebookContact[]): Map<string, number> {
	const counts = new Map<string, number>()

	for (const contact of contacts) {
		for (const id of contact.groups ?? []) {
			counts.set(id, (counts.get(id) ?? 0) + 1)
		}
	}
	return counts
}

/**
 * Add or replace a group
 * @param groups The groups
 * @param group The new or changed group
 * @returns A new array, sorted by name
 */
export function upsertGroup(groups: PbxPhonebookGroup[], group: PbxPhonebookGroup): PbxPhonebookGroup[] {
	return sortGroups([...groups.filter(existing => existing._id !== group._id), group])
}

/**
 * The group ids to save on a contact - drops groups that were deleted, or are of the wrong kind, while it was edited
 * @param ids The contact's group ids
 * @param available The groups the contact can be in
 * @returns The ids that are among the available groups
 */
export function keepKnownGroups(ids: string[] | null | undefined, available: PbxPhonebookGroup[]): string[] {
	const known = new Set(available.map(group => group._id))

	return (ids ?? []).filter(id => known.has(id))
}

/**
 * The paths a tcx-api error points at: its own and those of its inner errors
 * @param error The API error
 * @returns The paths (or codes, when there is no path)
 */
function errorPaths(error: Partial<ErrorResponseDetail>): string[] {
	return [error.path ?? error.code, ...(error.inner ?? []).map(inner => inner.path ?? inner.code)].filter((path): path is string => !!path)
}

/**
 * Why a create, rename or delete of a group was refused
 * @param err What the API store threw
 * @returns The reason
 */
export function groupErrorKey(err: unknown): PhonebookGroupErrorKey {
	const error = (err ?? {}) as Partial<ErrorResponseDetail>

	switch (error.status) {
		case 0: return 'network'
		case 403: return 'accessDenied'
		case 404: return 'notFound'
		case 409: return 'nameTaken'
		case 422: return errorPaths(error).some(path => path.split('.').pop() === 'name') ? 'nameInvalid' : 'unknown'
		default: return 'unknown'
	}
}

/**
 * Was a contact save refused because of its groups (unknown group, wrong kind, more than 100)?
 * @param err What the API store threw
 * @returns True for a 422 on groups or groups[i]
 */
export function isContactGroupsError(err: unknown): boolean {
	const error = (err ?? {}) as Partial<ErrorResponseDetail>

	return error.status === 422 && errorPaths(error).some(path => /^(body\.)?groups(\[\d+\])?$/.test(path))
}
